/** @format */
import { DashboardView } from '@/app/[locale]/(protected)/(dashboard)/page';
import { useConfigStore } from '@/providers/ConfigStoreProvider';
import { useIsAdmin } from '@common/application/hooks/useIsAdmin';
import { CompanyClient } from '@common/model/apiClient';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { motion } from 'motion/react';
import Image from 'next/legacy/image';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import Skeleton from 'react-loading-skeleton';
import styles from './DashboardSidebar.module.css';

interface SidebarProps {
  view: DashboardView;
  setView: (view: DashboardView) => void;
}

export enum DashboardLink {
  ACCOUNTS = 'accounts',
  DOCS = 'documents',
  COMPANY = 'companyDashboard',
  SEARCH = 'searchAccounts',
}

function DashboardSidebar(props: SidebarProps) {
  const { isAdmin } = useIsAdmin();
  const companyId = useConfigStore((s) => s.company);

  const links = [
    { id: DashboardLink.ACCOUNTS, label: <FormattedMessage defaultMessage="Accounts" description="Accounts admin page title" />, href: DashboardView.ACCOUNTS },
    { id: DashboardLink.DOCS, label: <FormattedMessage defaultMessage="Documents" description="Account page documents header" />, href: DashboardView.DOCUMENTS },
    { id: DashboardLink.COMPANY, label: 'Company Dashboard', adminOnly: true, href: DashboardView.COMPANY },
  ];

  const { isLoading: isCompanyFetching, data: companyData } = useQuery({
    queryKey: ['get_company', companyId],
    queryFn: () => CompanyClient.getById(companyId),
    enabled: !!companyId,
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  const logo = companyData?.data?.payload?.logo?.url as string;

  return (
    <div className={styles.sidebar}>
      <div className={styles.logo}>
        <div className={styles.plate}>{!logo ? <Skeleton width={160} height={80} /> : <Image src={logo ?? ''} layout="fill" objectFit="contain" alt={'Company Logo'} />}</div>
      </div>
      <div className={styles.nav}>
        {links
          .filter((link) => !link.adminOnly || (link.adminOnly && isAdmin))
          .map((link) => {
            const isSelected = props.view === link.href;
            const className = clsx(styles.link, { [styles.selected]: isSelected });
            return (
              <div
                className={className}
                key={'tab' + link.id}
                onClick={() => {
                  props.setView(link.href);
                }}
              >
                <span>{link.label}</span>
                {isSelected && (
                  <motion.div className={styles.highlight} layoutId={'tabHighlight'}>
                    <span></span>
                  </motion.div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}

export default DashboardSidebar;
